/**
 * Apollo Client for Gatsby + WooCommerce + WPGraphQL JWT
 */

import fetch from "isomorphic-fetch";
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client";
import { isEmpty } from "lodash";
import { retryRefreshLink } from "./retryRefreshLink";
import { authStore } from "../utils/tokenStore";

/**
 * Middleware → adds WooCommerce session + JWT token
 */
const middleware = new ApolloLink((operation, forward) => {
  let headers = {};

  // WooCommerce session
  const session =
    typeof window !== "undefined" ? localStorage.getItem("woo-session") : null;

  if (!isEmpty(session)) {
    headers["woocommerce-session"] = `Session ${session}`;
  }

  // JWT Auth token
  authStore.load();
  const token = authStore.getToken();

  if (!isEmpty(token)) {
    headers["authorization"] = `Bearer ${token}`;
  }

  operation.setContext(({ headers: existing = {} }) => ({
    headers: {
      ...existing,
      ...headers,
    },
  }));

  return forward(operation);
});

/**
 * Afterware → Sync WooCommerce session header to localStorage
 */
const afterware = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const { headers } = context?.response || {};

    if (!headers) return response;

    const session = headers.get("woocommerce-session");

    if (session) {
      if (session === "false") {
        localStorage.removeItem("woo-session");
      } else if (localStorage.getItem("woo-session") !== session) {
        localStorage.setItem("woo-session", session);
      }
    }

    return response;
  });
});

// HTTP link
const httpLink = createHttpLink({
  uri: process.env.GATSBY_WPGRAPHQL_URL,
  fetch,
});

/**
 * Apollo Client instance holder (so retry link can access it later)
 */
let apolloClient = null;

/**
 * Build client
 */
export const client = new ApolloClient({
  link: ApolloLink.from([
    retryRefreshLink(() => apolloClient), // <-- automatic JWT refresh + retry
    middleware,
    afterware,
    httpLink,
  ]),
  cache: new InMemoryCache(),
});

// Assign the instance so that retryRefreshLink can access it
apolloClient = client;
